/**
 * Die **Tastenkürzel** des Aufgaben-Menüs — die Zeichen im Menü und die Tasten,
 * die dieselbe Sache ohne Menü tun, aus EINER Quelle.
 *
 * **Neu gesetzt am 05.10.2026 (#257)**, gemeinsam mit dem Menü „Aufgaben“ der Menüleiste. Das macOS-Menü
 * fängt Einzeltasten ab, auch in Textfeldern — dort stehen deshalb nur Kürzel mit ⌘. Steht ein
 * Einzelbuchstabe daneben, gilt er in der Liste und im Web; sonst gilt das ⌘-Kürzel überall:
 *
 * | Befehl            | Liste + Web | Menü  |
 * |-------------------|-------------|-------|
 * | Wann              | S           | ⇧⌘S   |
 * | Deadline          | E           | ⇧⌘E   |
 * | Dauer             | D           | ⇧⌘D   |
 * | Tags              | T           | ⇧⌘T   |
 * | Wiederkehrend     | ⇧⌘R         | ⇧⌘R   |
 * | Aufmerksamkeit    | ⇧⌘A         | ⇧⌘A   |
 * | Belohnung         | ⇧⌘G         | ⇧⌘G   |
 * | Fokus starten     | P           | ⌘P    |
 * | Ruhen / Aufwecken | ⇧⌘L         | ⇧⌘L   |
 * | Favorit           | ⇧⌘F         | ⇧⌘F   |
 * | Details           | ⌘I          | ⌘I    |
 * | Im Projekt zeigen | ⌘J          | ⌘J    |
 * | Verschieben       | ⇧⌘M         | ⇧⌘M   |
 *
 * Tabu als Einzeltaste (Aufgabenliste im Fokus, kein Eingabefeld): `A`, `B`, `F` legen eine Aufgabe an,
 * `⇧A`/`⇧B`/`⇧F` einen Abschnitt, `L` wechselt den Lebensbereich, `N`/`M` holen Notiz und Memo auf die
 * Pinnwand, `⇧S` zieht dort einen Bereich auf, `Q` öffnet die Schnelleingabe, `K` das Klären.
 *
 * Bewusst ohne React-Bezug — eine Tabelle, kein Verhalten.
 */

import type { TaskMenuTarget } from './TaskMenu.types'
import { detectOS } from '@/src/renderer/lib/platform/detection'

/** Was im Menü rechts in der Zeile steht — in Mac-Schreibweise, `shortcutText` macht daraus Windows. */
export const TASK_MENU_SHORTCUTS: Record<TaskMenuTarget | 'rest' | 'duplicate' | 'delete' | 'focus' | 'nest' | 'favorite' | 'details' | 'showInProject', string> = {
  start: 'S',
  duration: 'D',
  deadline: 'E',
  recurrence: '⇧⌘R',
  attention: '⇧⌘A',
  tags: 'T',
  reward: '⇧⌘G',
  rest: '⇧⌘L',
  focus: 'P',
  favorite: '⇧⌘F',
  details: '⌘I',
  showInProject: '⌘J',
  move: '⇧⌘M',
  duplicate: '⌘D',
  // Gebunden in der Liste (`useProjectNavigation`), nicht über `taskMenuTargetForKey`.
  nest: '⇧⌥↑',
  delete: '⌫',
}

/**
 * Eine Taste. `mod` ist ⌘ auf dem Mac, Strg unter Windows.
 *
 * Geprüft wird `event.code`, nicht `event.key`: Auf einer deutschen Tastatur liefert `key` bei
 * gedrückter Umschalttaste den Großbuchstaben, und der Mac meldet ⇧⌘D als „d“. `code` sagt, welche
 * TASTE gedrückt wurde, die Umschalttaste steht daneben.
 */
export interface TaskKey {
  code: string
  shift: boolean
  mod: boolean
}

export interface TaskMenuKey extends TaskKey {
  target: TaskMenuTarget
}

const letter = (code: string, target: TaskMenuTarget): TaskMenuKey => ({ code, shift: false, mod: false, target })
const shiftMod = (code: string, target: TaskMenuTarget): TaskMenuKey => ({ code, shift: true, mod: true, target })

/** Welche Taste welches Unter-Menü öffnet. */
export const TASK_MENU_KEYS: readonly TaskMenuKey[] = [
  letter('KeyS', 'start'), shiftMod('KeyS', 'start'),
  letter('KeyE', 'deadline'), shiftMod('KeyE', 'deadline'),
  letter('KeyD', 'duration'), shiftMod('KeyD', 'duration'),
  letter('KeyT', 'tags'), shiftMod('KeyT', 'tags'),
  shiftMod('KeyR', 'recurrence'),
  shiftMod('KeyA', 'attention'),
  shiftMod('KeyG', 'reward'),
  // Blankes `M` ist das Memo (#229) — Verschieben trägt deshalb ⌘, wie bei Things.
  shiftMod('KeyM', 'move'),
]

/** Ruhen lassen bzw. Aufwecken — eine Handlung, kein Unter-Menü. */
export const TASK_MENU_REST_KEYS: readonly TaskKey[] = [{ code: 'KeyL', shift: true, mod: true }]

/**
 * Der **Fokus** — „**P**lay". `P` gehörte bis zum 16.09.2026 dem Anheften eines Tabs; Play ist
 * wichtiger. ⌘P steht im Menü (im Browser wäre es Drucken — die Liste fängt es ab).
 */
export const TASK_MENU_FOCUS_KEYS: readonly TaskKey[] = [
  { code: 'KeyP', shift: false, mod: false },
  { code: 'KeyP', shift: false, mod: true },
]

/** Der Stern (#210). Bis #257 ⌥F — im Textfeld ein „ƒ“, und das macOS-Menü fing es ab. */
export const TASK_MENU_FAVORITE_KEY: TaskKey = { code: 'KeyF', shift: true, mod: true }

/** Die Aufgaben-Details auf- und zuklappen (#257) — „Info“ wie im Finder. */
export const TASK_MENU_DETAILS_KEY: TaskKey = { code: 'KeyI', shift: false, mod: true }

/** Im Projekt zeigen (#257) — „Jump“. */
export const TASK_MENU_SHOW_IN_PROJECT_KEY: TaskKey = { code: 'KeyJ', shift: false, mod: true }

type KeyEvent = { code: string; shiftKey: boolean; ctrlKey: boolean; metaKey: boolean; altKey: boolean }

const IS_MAC = detectOS() === 'mac'

/** Passt der Tastendruck genau? Andere Modifikatoren schließen aus: `⌘D` dupliziert, `D` ist die Dauer. */
export function matchesTaskKey(event: KeyEvent, key: TaskKey, mac = IS_MAC): boolean {
  if (event.code !== key.code || event.shiftKey !== key.shift || event.altKey) return false
  if (!key.mod) return !event.metaKey && !event.ctrlKey
  return mac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey
}

/** Welches Ziel eine Tastenkombination meint — `null`, wenn keine passt. */
export function taskMenuTargetForKey(event: KeyEvent, mac = IS_MAC): TaskMenuTarget | null {
  return TASK_MENU_KEYS.find(key => matchesTaskKey(event, key, mac))?.target ?? null
}
